import { createToast } from '@/utils'
import type { FC, ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import { useRouter } from '@/hooks/use-router'
import { paths } from '@/paths'
import useTranslate from '@/hooks/use-translate'
import { Role } from '@/api'
import { isDocumentsReturnUrlPath } from '@/routes/documents-editor-paths'
import {
  buildLoginUrlWithReturn,
  getCurrentReturnPath,
} from '@/utils/auth-return-url'

interface AuthGuardProps {
  children: ReactNode
  role: Role[] | null
}

export const AuthGuard: FC<AuthGuardProps> = (props) => {
  const { children } = props
  const router = useRouter()
  const location = useLocation()
  const { isAuthenticated, issuer, user } = useAuth()

  const { t: tCommon } = useTranslate('common')

  const [checked, setChecked] = useState(false)

  const check = useCallback(() => {
    if (!isAuthenticated) {
      if (isDocumentsReturnUrlPath(location.pathname)) {
        const returnPath = getCurrentReturnPath(
          location.pathname,
          location.search,
          location.hash
        )
        router.replace(buildLoginUrlWithReturn(returnPath))
      } else {
        router.replace(paths.auth.jwt.login)
      }
    } else if (props.role && !props.role.includes(user?.role as Role)) {
      router.back()
      createToast('error', tCommon('permissionError'))
    } else {
      setChecked(true)
    }
  }, [isAuthenticated, issuer, location, router])

  // Only check on mount, this allows us to redirect the user manually when auth state changes
  useEffect(
    () => {
      check()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  if (!checked) {
    return null
  }

  // If got here, it means that the redirect did not occur, and that tells us that the user is
  // authenticated / authorized.

  return <>{children}</>
}
